import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem, CommandGroup } from "./components/ui/command";
import { ArrowLeft, Bus, MapPin, Navigation, AlertCircle, Loader2, CheckCircle, XCircle, ChevronDown, ChevronRight, X, Search, ChevronsUpDown, Download, Info } from "lucide-react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup, useMap } from "react-leaflet";
import { toast } from "sonner";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import { DatePicker } from "./DatePicker";

const API = process.env.REACT_APP_BACKEND_URL;

// Per-tab BUID resolution — reads sessionStorage only (same rule as App.js).
const getBUID = () => sessionStorage.getItem("routeoptima_buid") || "";

// Compact BUID badge for the page header.
function BuidBadge() {
  const buid = getBUID();
  if (!buid) return null;
  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-200 bg-slate-50"
      data-testid="header-buid-badge"
      title={`Client: ${buid}`}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Client</span>
      <span className="text-xs font-mono font-semibold text-slate-700 max-w-[180px] truncate">{buid}</span>
    </div>
  );
}

function MapBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length >= 2) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [bounds, map]);
  return null;
}

export default function VehicleTracking({ onBack }) {
  const [loading, setLoading] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [tripDetail, setTripDetail] = useState(null);
  const [tripLoading, setTripLoading] = useState(false);
  const fetchingRef = useRef(false);

  // Available dates from GPS data
  const [availableDates, setAvailableDates] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [availableDirections, setAvailableDirections] = useState([]);

  // Filters
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedShift, setSelectedShift] = useState("");
  const [selectedDirection, setSelectedDirection] = useState("");
  const [searchStop, setSearchStop] = useState("");
  const [searchBus, setSearchBus] = useState("");
  const [stopOpen, setStopOpen] = useState(false);
  const [busOpen, setBusOpen] = useState(false);

  // Expanded route cards
  const [expandedRoute, setExpandedRoute] = useState(null);
  const [showLegend, setShowLegend] = useState(true);

  // Unknown stops download
  const [unkDownloading, setUnkDownloading] = useState(false);
  const [unkProgress, setUnkProgress] = useState({ progress: 0, stage: "" });

  // Trip-data Excel download
  const [tripsDownloading, setTripsDownloading] = useState(false);

  // Load available dates on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/vehicle-tracking/trips`);
        const data = await res.json();
        setAvailableDates(data.available_dates || []);
        setAvailableShifts(data.available_shifts || []);
        setAvailableDirections(data.available_directions || []);
      } catch { /* ignore */ }
    })();
  }, []);

  // Async-job progress for the cold path (warm reads still come back inline).
  const [analysisProgress, setAnalysisProgress] = useState({ pct: 0, stage: "" });
  const pollRef = useRef(null);

  // Load route analysis when date changes
  const loadAnalysis = useCallback(async () => {
    if (!selectedDate) return;
    setLoading(true);
    setRouteData(null);
    setTripDetail(null);
    setExpandedRoute(null);
    setAnalysisProgress({ pct: 0, stage: "" });

    // Stop any earlier poll loop. Switching date/shift/direction starts fresh.
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }

    try {
      // `async: true` flips the endpoint into job mode for cold paths.
      // Warm cache hits still return 200 + payload directly; only the cold
      // path returns 202 + job_id so we don't run into the ingress 30s
      // timeout that produced the misleading 502 the user reported.
      const resp = await axios.post(`${API}/api/vehicle-tracking/route-analysis`, {
        date: selectedDate,
        shift: selectedShift || "",
        direction: selectedDirection || "",
        async: true,
      }, { timeout: 30000 });

      if (resp.status === 200) {
        // Warm cache hit — done.
        setRouteData(resp.data);
        setLoading(false);
        return;
      }

      // 202 → poll for completion. Server-side typical: 20-30s.
      const jobId = resp.data.job_id;
      setAnalysisProgress({
        pct: resp.data.progress || 0,
        stage: resp.data.stage || "Starting...",
      });

      pollRef.current = setInterval(async () => {
        try {
          const status = await axios.get(
            `${API}/api/vehicle-tracking/route-analysis/status`,
            { params: { job_id: jobId }, timeout: 10000 },
          );
          const j = status.data;
          if (j.status === "running") {
            setAnalysisProgress({ pct: j.progress || 0, stage: j.stage || "" });
          } else if (j.status === "complete") {
            clearInterval(pollRef.current); pollRef.current = null;
            setRouteData(j.result);
            setLoading(false);
          } else if (j.status === "error") {
            clearInterval(pollRef.current); pollRef.current = null;
            toast.error(`Route analysis failed: ${j.error || "Unknown error"}`);
            setLoading(false);
          }
        } catch (e) {
          // Transient poll error — keep trying. Only abort on persistent
          // failure (the job itself either completes or marks error).
          console.warn("route-analysis status poll failed (will retry):", e?.message);
        }
      }, 2000);
    } catch (e) {
      console.error("Route analysis error:", e);
      const msg = e.response?.data?.detail || e.message || "Unknown error";
      toast.error(`Route analysis failed: ${msg}`);
      setLoading(false);
    }
  }, [selectedDate, selectedShift, selectedDirection]);

  // Cleanup poll on unmount.
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    // Reset search filters when date changes
    if (selectedDate) { setSearchStop(""); setSearchBus(""); }
  }, [selectedDate]);

  // Load trip detail for a specific bus on a route
  const loadTripDetail = async (busId, shift, direction) => {
    if (!selectedDate || fetchingRef.current) return;
    fetchingRef.current = true;
    setTripLoading(true);
    setTripDetail(null);
    try {
      const { data } = await axios.post(`${API}/api/vehicle-tracking/trip-detail`, {
        bus_id: busId, date: selectedDate, shift, direction,
      });
      setTripDetail(data);
      setShowLegend(true);
    } catch (e) {
      console.error("Trip detail error:", e);
      const msg = e.response?.data?.detail || e.message || "Unknown error";
      toast.error(`Trip detail failed: ${msg}`);
    } finally {
      setTripLoading(false);
      fetchingRef.current = false;
    }
  };

  // Load GPS route for an unplanned vehicle
  const loadUnplannedTripDetail = async (imei, shift, direction, stops, stopVisits) => {
    if (!selectedDate || fetchingRef.current) return;
    fetchingRef.current = true;
    setTripLoading(true);
    setTripDetail(null);
    try {
      const { data } = await axios.post(`${API}/api/vehicle-tracking/unplanned-trip-detail`, {
        imei, date: selectedDate, shift, direction, stops, stop_visits: stopVisits || [],
      });
      setTripDetail(data);
      setShowLegend(true);
    } catch (e) {
      console.error("Unplanned trip detail error:", e);
      const msg = e.response?.data?.detail || e.message || "Unknown error";
      toast.error(`Unplanned trip failed: ${msg}`);
    } finally {
      setTripLoading(false);
      fetchingRef.current = false;
    }
  };

  // Download trip-data Excel (one row per (route, bus, shift, direction))
  const handleDownloadTripData = async () => {
    if (!selectedDate) {
      toast.error("Select a date first");
      return;
    }
    setTripsDownloading(true);
    try {
      const params = new URLSearchParams({ date: selectedDate });
      if (selectedShift) params.append("shift", selectedShift);
      if (selectedDirection) params.append("direction", selectedDirection);
      const buidHeader = getBUID() ? { "X-BUID": getBUID() } : {};
      const downloadUrl = `${API}/api/vehicle-tracking/trips/download?${params.toString()}`;

      let res = await fetch(downloadUrl, { headers: buidHeader });

      // 409 = "Route analysis cache not warm" — happens when a background
      // job (auto-sync / retention / process-data) invalidated the cache
      // after the user warmed it. Kick a background warm job and poll for
      // completion. Async pattern avoids the ingress 30s timeout that
      // produced the "Couldn't warm route analysis" toast on cold caches.
      if (res.status === 409) {
        toast.message("Computing route analysis…", {
          description: "Cache was cleared — warming it now (this can take up to a minute).",
        });
        try {
          // Kick off the warm as an async job so a slow cold path can't
          // trip the 30s edge timeout.
          const warmStart = await fetch(`${API}/api/vehicle-tracking/route-analysis`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...buidHeader },
            body: JSON.stringify({
              date: selectedDate,
              shift: selectedShift || "",
              direction: selectedDirection || "",
              async: true,
            }),
          });

          if (warmStart.status === 200) {
            // Warm hit — no polling needed, cache is already populated.
            res = await fetch(downloadUrl, { headers: buidHeader });
          } else if (warmStart.status === 202) {
            // Background job kicked — poll status until complete/error.
            const startBody = await warmStart.json();
            const jobId = startBody.job_id;
            if (!jobId) throw new Error("warm job returned no job_id");

            // Poll every 2s, cap wait at 180s (3 min) so a stuck job
            // eventually surfaces an error instead of spinning forever.
            const deadline = Date.now() + 180_000;
            while (true) {
              if (Date.now() > deadline) {
                throw new Error("warm job timed out after 3 minutes");
              }
              await new Promise((r) => setTimeout(r, 2000));
              const statusRes = await fetch(
                `${API}/api/vehicle-tracking/route-analysis/status?job_id=${encodeURIComponent(jobId)}`,
                { headers: buidHeader },
              );
              if (!statusRes.ok) continue; // transient — retry
              const j = await statusRes.json();
              if (j.status === "complete") break;
              if (j.status === "error") {
                throw new Error(j.error || "warm job failed");
              }
              // else "running" — keep polling.
            }
            // Cache repopulated — retry the original download.
            res = await fetch(downloadUrl, { headers: buidHeader });
          } else {
            throw new Error(`warm HTTP ${warmStart.status}`);
          }
        } catch (warmErr) {
          console.warn("route-analysis warm failed:", warmErr?.message);
          toast.error("Couldn't warm route analysis. Please click Apply on the page and try again.");
          return;
        }
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        toast.error(err.detail || `Download failed (HTTP ${res.status})`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const suffix = [selectedDate, selectedShift, selectedDirection].filter(Boolean).join("_");
      link.download = `trips_${suffix}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Trip data downloaded");
    } catch (e) {
      toast.error("Trip data download failed");
    } finally {
      setTripsDownloading(false);
    }
  };

  // Download unknown stops as geocoded Excel
  const handleDownloadUnknownStops = async () => {
    if (!selectedDate) {
      toast.error("Select a date first");
      return;
    }
    setUnkDownloading(true);
    setUnkProgress({ progress: 0, stage: "Starting..." });
    try {
      await fetch(`${API}/api/vehicle-tracking/unknown-stops/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: selectedDate }),
      });

      // Poll for progress
      let attempts = 0;
      const maxAttempts = 600; // ~30 min (3s intervals) — geocoding can be slow
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const statusRes = await fetch(`${API}/api/vehicle-tracking/unknown-stops/status`);
          const task = await statusRes.json();
          setUnkProgress({ progress: task.progress || 0, stage: task.stage || "Processing..." });

          if (task.status === "completed") {
            // Download the Excel
            const dlRes = await fetch(`${API}/api/vehicle-tracking/unknown-stops/download`);
            const blob = await dlRes.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `unknown_stops_${selectedDate}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success(task.stage || "Download complete");
            setUnkDownloading(false);
            return;
          } else if (task.status === "failed") {
            toast.error(task.error || "Export failed");
            setUnkDownloading(false);
            return;
          } else if (task.status === "cancelled") {
            toast.info("Export cancelled");
            setUnkDownloading(false);
            return;
          }
        } catch (pollErr) {
          console.warn("Poll error:", pollErr);
        }
      }
      toast.error("Export timed out");
    } catch (error) {
      toast.error("Failed to start export");
    } finally {
      setUnkDownloading(false);
    }
  };

  const handleCancelUnknownStops = async () => {
    try {
      await fetch(`${API}/api/vehicle-tracking/unknown-stops/cancel`, { method: "POST" });
    } catch { /* ignore */ }
  };

  const filters = routeData?.filters || {};
  const allRoutes = routeData?.routes || [];
  const summary = routeData?.summary || {};

  // Build unique stop and bus lists from loaded route data
  const stopOptions = useMemo(() => {
    const stops = new Set();
    allRoutes.forEach(r => {
      (r.stops || []).forEach(s => {
        if (s && s !== "Unknown Stop" && !s.startsWith("Unk(")) stops.add(s);
      });
    });
    return Array.from(stops).sort();
  }, [allRoutes]);

  const busOptions = useMemo(() => {
    const buses = new Set();
    allRoutes.forEach(r => {
      (r.planned_buses || []).forEach(b => {
        if (b.vehicle_no) buses.add(b.vehicle_no);
      });
      (r.actual_vehicles || []).forEach(v => {
        if (v.vehicle_no) buses.add(v.vehicle_no);
      });
    });
    return Array.from(buses).sort();
  }, [allRoutes]);

  // Client-side filtering by selected stop or bus
  const routes = useMemo(() => {
    let filtered = allRoutes;
    if (searchStop) {
      filtered = filtered.filter(r =>
        (r.stops || []).some(s => s === searchStop)
      );
    }
    if (searchBus) {
      filtered = filtered.filter(r =>
        (r.planned_buses || []).some(b => b.bus_id === searchBus || b.vehicle_no === searchBus) ||
        (r.actual_vehicles || []).some(v => v.vehicle_no === searchBus)
      );
    }
    return filtered;
  }, [allRoutes, searchStop, searchBus]);

  // Map bounds
  const getMapBounds = () => {
    if (!tripDetail) return null;
    const points = [];
    (tripDetail.planned_stops || []).forEach(s => {
      if (s.latitude && s.longitude) points.push([s.latitude, s.longitude]);
    });
    (tripDetail.actual_route || []).forEach(p => points.push(p));
    (tripDetail.planned_route || []).forEach(p => points.push(p));
    if (tripDetail.office?.latitude && tripDetail.office?.longitude) {
      points.push([tripDetail.office.latitude, tripDetail.office.longitude]);
    }
    return points.length >= 2 ? points : null;
  };
  const bounds = getMapBounds();

  return (
    <div className="flex flex-col h-screen bg-background" data-testid="vehicle-tracking">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-card">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="tracking-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <Bus className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-semibold">Vehicle Tracking</h1>
        </div>
        <Badge variant="secondary" className="ml-2">Route View</Badge>

        {/* Summary badges */}
        {routeData && (
          <div className="ml-auto flex items-center gap-3 text-xs">
            <span className="text-muted-foreground">Routes: <strong>{summary.planned_routes}</strong></span>
            {summary.unplanned_routes > 0 && (
              <span className="text-amber-600">Unplanned: <strong>{summary.unplanned_routes}</strong></span>
            )}
            {(summary.total_deployed_buses > 0) && (
              <>
                <span className="text-muted-foreground">Deployed: <strong>{summary.total_deployed_buses}/{summary.total_planned_buses}</strong></span>
                <span className={summary.total_compliant === summary.total_deployed_buses ? "text-emerald-600" : "text-amber-600"}>
                  Compliant: <strong>{summary.total_compliant}/{summary.total_deployed_buses}</strong>
                </span>
              </>
            )}
          </div>
        )}
        <div className={routeData ? "ml-3" : "ml-auto"}>
          <BuidBadge />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left panel - Filters + Route List */}
        <div className="w-96 border-r bg-card flex flex-col overflow-hidden">
          {/* Filters */}
          <div className="p-3 border-b space-y-2" data-testid="tracking-filters">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Filters</div>

            {/* Calendar-driven date pick — user can select any date.
                Compute happens on Apply; an "available_dates" list is no
                longer maintained, so a fresh-deploy with no data still
                lets ops query yesterday and see "no data" rather than an
                empty dropdown. */}
            <DatePicker
              value={selectedDate}
              onChange={(d) => { setSelectedDate(d); setSelectedShift(""); setSelectedDirection(""); setRouteData(null); setTripDetail(null); }}
              placeholder="Select Date"
              disabled={loading}
              data-testid="filter-date"
            />

            <div className="grid grid-cols-2 gap-2">
              <Select value={selectedShift || "__all__"} onValueChange={v => setSelectedShift(v === "__all__" ? "" : v)} disabled={loading}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-shift">
                  <SelectValue placeholder="All Shifts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Shifts</SelectItem>
                  {(filters.shifts?.length ? filters.shifts : availableShifts).map(s => (
                    <SelectItem key={s} value={s}>{s} Shift</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedDirection || "__all__"} onValueChange={v => setSelectedDirection(v === "__all__" ? "" : v)} disabled={loading}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-direction">
                  <SelectValue placeholder="All Directions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Directions</SelectItem>
                  {(filters.directions?.length ? filters.directions : availableDirections).map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              className="w-full h-8 text-xs"
              disabled={!selectedDate || loading}
              onClick={loadAnalysis}
              data-testid="apply-filters-btn"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
              Apply
            </Button>

            {/* Trip-data download — available as soon as date is selected and analysis is loaded */}
            {selectedDate && routeData && (
              <div className="pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-8 text-xs"
                  disabled={tripsDownloading}
                  onClick={handleDownloadTripData}
                  data-testid="download-trips-btn"
                >
                  {tripsDownloading
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <Download className="w-3 h-3 mr-1" />}
                  {tripsDownloading ? "Preparing..." : "Download Trip Data"}
                </Button>
              </div>
            )}

            {/* Unknown Stops Download — available as soon as date is selected */}
            {selectedDate && (
              <div className="pt-1">
                {unkDownloading ? (
                  <Button variant="destructive" size="sm" className="w-full h-8 text-xs" onClick={handleCancelUnknownStops} data-testid="cancel-unk-download-btn">
                    <X className="w-3 h-3 mr-1" /> Cancel Download
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" className="w-full h-8 text-xs" onClick={handleDownloadUnknownStops} data-testid="download-unk-stops-btn">
                    <Download className="w-3 h-3 mr-1" /> Download Unknown Stops
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Unknown stops download progress */}
          {unkDownloading && (
            <div className="px-3 py-2 border-b space-y-1" data-testid="unk-download-progress">
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${unkProgress.progress}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{unkProgress.stage}</span>
                <span>{unkProgress.progress}%</span>
              </div>
            </div>
          )}

          {/* Post-load search filters — fixed below filters, above route list */}
          {routeData && (
            <div className="px-3 py-2 border-b space-y-1.5" data-testid="search-filters">
              <div className="flex gap-1">
                <Popover open={stopOpen} onOpenChange={setStopOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={stopOpen}
                      className="flex-1 h-7 text-xs justify-between font-normal"
                      data-testid="filter-stop"
                    >
                      {searchStop ? (
                        <span className="flex items-center gap-1.5 truncate">
                          <MapPin className="w-3 h-3 text-primary shrink-0" />
                          {searchStop}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Search by Stop...</span>
                      )}
                      <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Type stop name..." className="h-8 text-xs" />
                      <CommandList>
                        <CommandEmpty className="text-xs py-3 text-center">No stops found.</CommandEmpty>
                        <CommandGroup>
                          {stopOptions.map(s => (
                            <CommandItem
                              key={s}
                              value={s}
                              className="text-xs"
                              onSelect={() => { setSearchStop(s === searchStop ? "" : s); setStopOpen(false); }}
                            >
                              <MapPin className="w-3 h-3 mr-1.5 text-muted-foreground" />
                              {s}
                              {s === searchStop && <CheckCircle className="w-3 h-3 ml-auto text-primary" />}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {searchStop && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setSearchStop("")} data-testid="clear-stop">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>

              <div className="flex gap-1">
                <Popover open={busOpen} onOpenChange={setBusOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={busOpen}
                      className="flex-1 h-7 text-xs justify-between font-normal"
                      data-testid="filter-bus"
                    >
                      {searchBus ? (
                        <span className="flex items-center gap-1.5 truncate">
                          <Bus className="w-3 h-3 text-primary shrink-0" />
                          {searchBus}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Search by Bus...</span>
                      )}
                      <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Type bus/vehicle number..." className="h-8 text-xs" />
                      <CommandList>
                        <CommandEmpty className="text-xs py-3 text-center">No buses found.</CommandEmpty>
                        <CommandGroup>
                          {busOptions.map(b => (
                            <CommandItem
                              key={b}
                              value={b}
                              className="text-xs"
                              onSelect={() => { setSearchBus(b === searchBus ? "" : b); setBusOpen(false); }}
                            >
                              <Bus className="w-3 h-3 mr-1.5 text-muted-foreground" />
                              {b}
                              {b === searchBus && <CheckCircle className="w-3 h-3 ml-auto text-primary" />}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {searchBus && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setSearchBus("")} data-testid="clear-bus">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>

              <div className="text-[10px] text-muted-foreground">
                Showing {routes.length} of {allRoutes.length} routes
              </div>
            </div>
          )}

          {/* No-roster mode banner: shown when the selected date has zero
              rosters. Stop-visit detection still works (same swipe + GPS
              logic), but planned-vs-unplanned classification is skipped per
              the "no roster → all routes are unplanned" rule. */}
          {routeData && routeData.has_roster === false && (
            <div
              className="mx-2 mt-2 mb-1 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-2 flex items-start gap-2 text-[11px] text-blue-800"
              data-testid="no-roster-banner"
            >
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold">No roster for this date</div>
                <div className="text-blue-700/80 leading-snug">
                  Stops are detected from GPS + swipes. Without a roster we
                  can't verify which planned route a vehicle was supposed to
                  run, so all detected vehicles appear as <strong>unplanned</strong>.
                </div>
              </div>
            </div>
          )}

          {/* Route List */}
          <div className="flex-1 overflow-y-auto p-2 space-y-2" data-testid="route-list">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3 px-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                {analysisProgress.stage ? (
                  <>
                    <div className="text-xs text-muted-foreground text-center" data-testid="route-analysis-stage">
                      {analysisProgress.stage}
                    </div>
                    <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${analysisProgress.pct}%` }}
                        data-testid="route-analysis-progress-bar"
                      />
                    </div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">
                      {analysisProgress.pct}%
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-muted-foreground">Loading…</div>
                )}
              </div>
            ) : !routeData ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
                Select filters and click Apply to view routes
              </div>
            ) : routes.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                {(searchStop || searchBus) ? "No routes match your search." : "No routes found. Save bus assignments first."}
              </div>
            ) : (
              routes.map((route, idx) => {
                const isExpanded = expandedRoute === idx;
                const isUnplanned = route.is_unplanned;
                const deployedCount = route.deployed_count ?? route.planned_count;
                const complianceRate = deployedCount > 0
                  ? Math.round((route.compliance / deployedCount) * 100) : 0;
                const unplannedOnRoute = !isUnplanned ? (route.actual_vehicles || []).filter(v => !v.is_planned).length : 0;

                return (
                  <Card key={idx} className={`overflow-hidden ${isUnplanned ? "border-amber-300 bg-amber-50/30" : ""}`} data-testid={`route-card-${idx}`}>
                    <div
                      className="p-3 cursor-pointer hover:bg-accent/30 transition-colors"
                      onClick={() => setExpandedRoute(isExpanded ? null : idx)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold break-words" title={route.route_name}>
                            {isUnplanned && <span className="text-amber-600 mr-1">[UNPLANNED]</span>}
                            {route.route_name}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px] h-5">{route.shift}-{route.direction}</Badge>
                            <span className="text-[10px] text-muted-foreground">
                              <MapPin className="w-3 h-3 inline" /> {route.num_stops} stops
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isUnplanned ? (
                            <Badge variant="outline" className="text-[10px] h-5 border-amber-400 text-amber-700 whitespace-nowrap">
                              {route.actual_count} vehicle{route.actual_count !== 1 ? "s" : ""}
                            </Badge>
                          ) : (
                            <>
                              {deployedCount > 0 && (
                                <Badge
                                  variant={complianceRate === 100 ? "default" : complianceRate > 0 ? "secondary" : "destructive"}
                                  className="text-[10px] h-5 whitespace-nowrap"
                                >
                                  {`${route.compliance}/${deployedCount}`}
                                </Badge>
                              )}
                              {deployedCount > 0 && deployedCount < route.planned_count && (
                                <Badge variant="outline" className="text-[10px] h-5 text-muted-foreground whitespace-nowrap">
                                  +{route.planned_count - deployedCount} unassigned
                                </Badge>
                              )}
                              {unplannedOnRoute > 0 && (
                                <Badge variant="outline" className="text-[10px] h-5 border-amber-400 text-amber-700 bg-amber-50 whitespace-nowrap" data-testid={`unplanned-badge-${idx}`}>
                                  +{unplannedOnRoute} extra
                                </Badge>
                              )}
                            </>
                          )}
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className={`border-t px-3 pb-3 pt-2 space-y-2 ${isUnplanned ? "bg-amber-50/40" : "bg-accent/10"}`}>
                        {/* Planned buses (only for planned routes) */}
                        {!isUnplanned && route.planned_buses.length > 0 && (
                          <>
                            <div className="text-[10px] font-semibold text-muted-foreground uppercase">Planned Buses</div>
                            {route.planned_buses.map((bus, bi) => {
                              const isDeployed = !!bus.imei;
                              return (
                              <div
                                key={bi}
                                className={`flex items-center gap-2 text-xs p-1.5 rounded transition-colors ${
                                  isDeployed ? "hover:bg-accent/30 cursor-pointer" : "opacity-60"
                                }`}
                                onClick={() => isDeployed && loadTripDetail(bus.bus_id, route.shift, route.direction)}
                                data-testid={`planned-bus-${bus.bus_id}`}
                              >
                                {isDeployed ? (
                                  bus.actually_ran ? (
                                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                  ) : (
                                    <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                  )
                                ) : (
                                  <Bus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                )}
                                <span className="font-mono font-semibold">{bus.bus_id}</span>
                                <span className="text-muted-foreground truncate">{bus.vehicle_no || "—"}</span>
                                {bus.office_time_ist && (
                                  <span className="text-[10px] text-blue-600 font-medium whitespace-nowrap" data-testid={`bus-office-time-${bus.bus_id}`}>
                                    {route.direction === "LOGIN" ? "Arr" : "Dep"}: {bus.office_time_ist}
                                  </span>
                                )}
                                <span className={`ml-auto text-[10px] ${
                                  !isDeployed ? "text-muted-foreground" :
                                  bus.actually_ran ? "text-emerald-600" : "text-red-500"
                                }`}>
                                  {!isDeployed ? "Not assigned" : bus.actually_ran ? "Ran" : "Did not run"}
                                </span>
                              </div>
                              );
                            })}
                          </>
                        )}

                        {/* Vehicles on this route */}
                        {route.actual_vehicles.length > 0 && (
                          <>
                            <div className="text-[10px] font-semibold text-muted-foreground uppercase mt-2">
                              {isUnplanned ? "Vehicles Detected" : "Unplanned Vehicles"}
                            </div>
                            {route.actual_vehicles.filter(v => isUnplanned || !v.is_planned).map((v, vi) => (
                              <div
                                key={vi}
                                className="flex items-center gap-2 text-xs p-1.5 rounded bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                                onClick={() => loadUnplannedTripDetail(v.imei, route.shift, route.direction, (v.stop_visits || []).map(sv => sv.stop || sv.name))}
                                data-testid={`unplanned-vehicle-${v.imei}`}
                              >
                                <Navigation className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                <span className="font-medium">{v.vehicle_no || v.imei}</span>
                                {v.swipe_match_pct != null && (
                                  <span className={`text-[10px] font-semibold ${v.swipe_match_pct >= 90 ? "text-emerald-600" : v.swipe_match_pct >= 75 ? "text-amber-600" : "text-red-500"}`}>
                                    {v.swipe_match_pct}% match
                                  </span>
                                )}
                                {v.office_time_ist && (
                                  <span className="text-[10px] text-blue-600 font-medium whitespace-nowrap" data-testid={`vehicle-office-time-${v.imei}`}>
                                    {route.direction === "LOGIN" ? "Arr" : "Dep"}: {v.office_time_ist}
                                  </span>
                                )}
                                <span className="ml-auto text-[10px] text-amber-600">
                                  View route
                                </span>
                              </div>
                            ))}
                          </>
                        )}

                        {/* Stops list */}
                        <div className="text-[10px] font-semibold text-muted-foreground uppercase mt-2">Stops</div>
                        <div className="flex flex-wrap gap-1">
                          {route.stops.map((s, si) => (
                            <Badge
                              key={si}
                              variant="outline"
                              className={`text-[10px] ${s === "Unknown Stop" || s.startsWith("Unk(") ? "border-red-300 text-red-600 bg-red-50" : ""}`}
                              data-testid={`stop-badge-${si}`}
                            >
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        </div>

        {/* Right panel - Map */}
        <div className="flex-1 relative">
          {tripLoading && (
            <div className="absolute inset-0 z-[1000] bg-background/60 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {!tripDetail && !tripLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-[500]">
              <div className="text-center text-muted-foreground space-y-2">
                <Bus className="w-12 h-12 mx-auto text-muted-foreground/30" />
                <p className="text-sm">Expand a route and click a bus to view on map</p>
              </div>
            </div>
          )}

          <MapContainer
            center={[12.5, 78.0]}
            zoom={8}
            className="h-full w-full"
            style={{ background: "#f8fafc" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://osm.org">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {bounds && <MapBounds bounds={bounds} />}

            {/* Planned route - dotted line */}
            {tripDetail?.planned_route?.length >= 2 && (
              <Polyline
                positions={tripDetail.planned_route}
                pathOptions={{ color: "#6366f1", weight: 4, dashArray: "10 8", opacity: 0.8 }}
              />
            )}

            {/* Actual route - solid line */}
            {tripDetail?.actual_route?.length >= 2 && (
              <Polyline
                positions={tripDetail.actual_route}
                pathOptions={{ color: "#ef4444", weight: 3, opacity: 0.9 }}
              />
            )}

            {/* Planned stops */}
            {tripDetail?.planned_stops?.map((stop, i) => (
              <CircleMarker
                key={`stop-${i}`}
                center={[stop.latitude, stop.longitude]}
                radius={stop.is_unknown ? 7 : (stop.visited ? 8 : 6)}
                pathOptions={{
                  color: stop.is_unknown ? "#dc2626" : (tripDetail.is_unplanned ? "#f59e0b" : (stop.visited ? "#10b981" : "#6366f1")),
                  fillColor: stop.is_unknown ? "#dc2626" : (tripDetail.is_unplanned ? "#f59e0b" : (stop.visited ? "#10b981" : "#6366f1")),
                  fillOpacity: 0.8,
                  weight: 2,
                }}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-semibold">
                      {stop.is_unknown ? (
                        <span style={{ color: "#dc2626" }}>Unknown Stop</span>
                      ) : (
                        stop.name || `Stop ${i + 1}`
                      )}
                    </div>
                    {stop.time && <div>Planned: {stop.time}</div>}
                    {!stop.is_unknown && (
                      <div>
                        Status: {stop.visited ? (
                          <span style={{ color: "#10b981" }}>
                            Visited{stop.visit_method ? ` (${stop.visit_method})` : ""}
                          </span>
                        ) : (
                          <span style={{ color: "#ef4444" }}>Not visited</span>
                        )}
                      </div>
                    )}
                    {stop.is_unknown && (
                      <div>
                        <span style={{ color: "#dc2626" }}>{stop.latitude.toFixed(4)}, {stop.longitude.toFixed(4)}</span>
                      </div>
                    )}
                    {stop.visit_time_ist && (
                      <div>Visit time: <strong>{stop.visit_time_ist} IST</strong></div>
                    )}
                    {stop.min_gps_distance_km != null && (
                      <div>Closest GPS: {(stop.min_gps_distance_km * 1000).toFixed(0)}m</div>
                    )}
                    {stop.min_swipe_distance_km != null && (
                      <div>Closest Swipe: {(stop.min_swipe_distance_km * 1000).toFixed(0)}m</div>
                    )}
                    {stop.unique_swipes > 0 && (
                      <div>Unique Swipes: <strong>{stop.unique_swipes}</strong></div>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            ))}

            {/* Office marker */}
            {tripDetail?.office && (
              <CircleMarker
                center={[tripDetail.office.latitude, tripDetail.office.longitude]}
                radius={10}
                pathOptions={{
                  color: "#f97316",
                  fillColor: "#f97316",
                  fillOpacity: 0.9,
                  weight: 3,
                }}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-semibold">{tripDetail.office.name || "Office"}</div>
                    <div>{tripDetail.direction === "LOGIN" ? "Destination" : "Origin"}</div>
                    {tripDetail.office.visit_time_ist && (
                      <div>
                        {tripDetail.direction === "LOGIN" ? "Arrival" : "Departure"}: <strong>{tripDetail.office.visit_time_ist} IST</strong>
                      </div>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            )}
          </MapContainer>

          {/* Legend + Stats overlay */}
          {tripDetail && showLegend && (
            <div className="absolute bottom-4 left-4 z-[1000] bg-card/95 backdrop-blur-sm border rounded-lg p-3 shadow-lg max-w-xs" data-testid="map-legend">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs font-semibold">
                  {tripDetail.is_unplanned ? (
                    <>{tripDetail.vehicle_no || tripDetail.imei} <span className="text-amber-600">[UNPLANNED]</span></>
                  ) : (
                    <>{tripDetail.bus_id} - {tripDetail.vehicle_no || "N/A"}</>
                  )}
                  {" | "}{tripDetail.shift}-{tripDetail.direction} | {tripDetail.date}
                </div>
                <button
                  onClick={() => setShowLegend(false)}
                  className="shrink-0 p-0.5 rounded hover:bg-accent transition-colors"
                  data-testid="legend-close-btn"
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </div>

              <div className="space-y-1 text-[11px]">
                {!tripDetail.is_unplanned && tripDetail.planned_route?.length >= 2 && (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-0.5 border-t-2 border-dashed" style={{ borderColor: "#6366f1" }} />
                    <span>Planned Route</span>
                  </div>
                )}
                {tripDetail.is_unplanned && tripDetail.planned_route?.length >= 2 && (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-0.5 border-t-2 border-dashed" style={{ borderColor: "#6366f1" }} />
                    <span>Planned Route</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5" style={{ backgroundColor: "#ef4444" }} />
                  <span>Actual GPS Route</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tripDetail.is_unplanned ? "#f59e0b" : "#6366f1" }} />
                  <span>{tripDetail.is_unplanned ? "Detected Stop" : "Planned Stop (not visited)"}</span>
                </div>
                {!tripDetail.is_unplanned && (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#10b981" }} />
                    <span>Visited Stop</span>
                  </div>
                )}
                {tripDetail.planned_stops?.some(s => s.is_unknown) && (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#dc2626" }} />
                    <span>Unknown Stop</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#f97316" }} />
                  <span>Office ({tripDetail.direction === "LOGIN" ? "Destination" : "Origin"})</span>
                </div>
              </div>

              {tripDetail.planned_stops?.length > 0 && (
                <div className="mt-2 pt-2 border-t text-[11px]">
                  <span className="text-muted-foreground">Stops: </span>
                  <span className="font-medium">
                    {tripDetail.planned_stops.filter(s => s.visited).length}/{tripDetail.planned_stops.length} visited
                  </span>
                </div>
              )}

              {tripDetail.message && (
                <div className="mt-2 pt-2 border-t text-[11px] text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {tripDetail.message}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
